"use client";

import Image from "next/image";
import NextLink from "next/link";
import { Fraunces } from "next/font/google";
import { Badge, Box, Button, Grid, Heading, HStack, Stack, Text } from "@chakra-ui/react";
import type { BlogPost } from "@/app/models/blog.models";
import { toPlainText } from "@/app/lib/blog-text";
import { getBlogCoverImageUrl } from "@/app/lib/blog-media";
import { useLoading } from "@/app/context/LoadingProvider";

type BlogHeroProps = {
  post: BlogPost;
};

const blogTitleFont = Fraunces({
  subsets: ["latin"],
  weight: ["600", "700", "900"],
});

const formatPublishDate = (date: string) =>
  new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
  }).format(new Date(date));

export function BlogHero({ post }: BlogHeroProps) {
  const imageUrl = getBlogCoverImageUrl(post.slug);
  const { setPageLoading } = useLoading();

  return (
    <Box
      as="section"
      rounded="3xl"
      border="1px solid"
      borderColor="blackAlpha.200"
      overflow="hidden"
      bgGradient="linear(135deg, rgba(56, 189, 248, 0.14), rgba(255, 255, 255, 0.86) 45%, rgba(249, 115, 22, 0.14))"
      _dark={{
        borderColor: "whiteAlpha.300",
        bgGradient:
          "linear(135deg, rgba(56, 189, 248, 0.16), rgba(15, 23, 42, 0.92) 45%, rgba(249, 115, 22, 0.16))",
      }}
    >
      <Grid
        templateColumns={{ base: "1fr", lg: "1.1fr 1fr" }}
        alignItems="stretch"
        position="relative"
      >
        <Stack
          gap={5}
          p={{ base: 6, md: 8, lg: 10 }}
          justify="center"
          order={{ base: 2, lg: 1 }}
        >
          <Badge alignSelf="flex-start" bg="whiteAlpha.200" color="cyan.100" rounded="full" px={3} py={1.5}>
            Featured
          </Badge>
          <Heading
            as="h1"
            fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
            lineHeight="1.2"
            className={`${blogTitleFont.className} text-balance bg-linear-to-r from-blue-700 via-violet-700 to-fuchsia-700 dark:from-blue-300 dark:via-violet-300 dark:to-fuchsia-300 bg-clip-text text-transparent`}
          >
            {post.title}
          </Heading>
          <Text fontSize={{ base: "md", md: "lg" }} lineHeight="1.8" color="fg.muted">
            {toPlainText(post.excerpt)}
          </Text>
          <HStack gap={4} flexWrap="wrap">
            <Button
              asChild
              colorPalette="cyan"
              size="lg"
              rounded="full"
              fontWeight="bold"
            >
              <NextLink
                href={`/blog/${post.slug}`}
                onClick={() => setPageLoading(true)}
              >
                Read article
              </NextLink>
            </Button>
            <Text fontSize="sm" color="fg.muted">
              Published {formatPublishDate(post.publishedAt)}
            </Text>
          </HStack>
        </Stack>
        <Box position="relative" minH={{ base: "260px", lg: "100%" }} order={{ base: 1, lg: 2 }}>
          <Image
            src={imageUrl}
            alt={post.title}
            fill
            sizes="(max-width: 992px) 100vw, 45vw"
            style={{ objectFit: "cover" }}
          />
          <Box
            position="absolute"
            inset={0}
            bgGradient="linear(to-l, rgba(0, 0, 0, 0.08), rgba(0, 0, 0, 0.4))"
          />
        </Box>
      </Grid>
    </Box>
  );
}
